import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import moment from 'moment';
import Button from '@material-ui/core/Button';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import MoveToInboxOutlinedIcon from '@material-ui/icons/MoveToInboxOutlined';
import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import HourglassEmptyOutlinedIcon from '@material-ui/icons/HourglassEmptyOutlined';
import LocationOffOutlinedIcon from '@material-ui/icons/LocationOffOutlined';
import BarChartOutlinedIcon from '@material-ui/icons/BarChartOutlined';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import Navbar from '../../components/navBar/navBar';
import ScopeFilters from '../../components/ScopeFilters/ScopeFilters';
import { apiList, verifyLogin } from '../../helpers';
import TrendChart from './TrendChart';
import DashboardListPanel from './DashboardListPanel';
import '../../assets/style/detailSection.scss';
import PageContainer from '../../components/PageContainer/PageContainer';

// Home dashboard: month-to-date counts, work waiting, and daily trends for
// the loans the user may see (scoped by the backend), filterable by branch
// and state.

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN');

const get = (url, params) => {
  const { session } = verifyLogin();
  return axios.get(url, { headers: { session }, params }).then((r) => r.data.data);
};

const RANGES = [
  { value: 'last30', label: 'Last 30 days' },
  { value: 'mtd', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
];

// Opens a card's list: small outlined button with an arrow, so it reads as
// an action.
function ViewAll({ onClick }) {
  return (
    <Button
      className='ds-btn home-view-all'
      variant='outlined'
      color='primary'
      size='small'
      endIcon={<ArrowForwardRoundedIcon />}
      onClick={onClick}
    >
      View all
    </Button>
  );
}

function Skeleton() {
  return <span className='ds-sk home-sk-value' />;
}

function MetricCard({
  title, icon: Icon, tone, value, loading,
}) {
  const lastMonth = moment().subtract(1, 'month').format('MMMM YYYY');
  return (
    <section className='home-card home-metric'>
      <div className='home-card-head'>
        <span className={`home-icon ${tone || ''}`} aria-hidden='true'><Icon /></span>
        <h2>{title}</h2>
      </div>
      <div className='home-value'>{loading ? <Skeleton /> : fmt(value?.mtd)}</div>
      <div className='home-caption'>This month</div>
      <div className='home-compare'>
        {`${lastMonth}: `}
        <strong>{loading ? '…' : fmt(value?.lastMonth)}</strong>
      </div>
    </section>
  );
}

function TrendCard({
  title, metric, color, params,
}) {
  const [range, setRange] = useState('last30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    get(apiList.DASHBOARD_TREND, { ...params, metric, range })
      .then((d) => { if (active) setData(d); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params, metric, range]);

  const peak = (data?.days || [])
    .reduce((best, d) => (d.count > (best?.count || 0) ? d : best), null);

  return (
    <section className='home-card'>
      <div className='home-card-head home-wrap'>
        <span className='home-icon' aria-hidden='true'><BarChartOutlinedIcon /></span>
        <h2>{title}</h2>
        <ToggleButtonGroup
          className='home-range'
          size='small'
          exclusive
          value={range}
          onChange={(e, value) => { if (value) setRange(value); }}
        >
          {RANGES.map((r) => <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>)}
        </ToggleButtonGroup>
      </div>
      <div className='home-chart' style={{ opacity: loading && data ? 0.55 : 1 }}>
        <div className='home-chart-total'>
          {peak && peak.count ? (
            <>
              {'Busiest day: '}
              <strong>{fmt(peak.count)}</strong>
              {` on ${moment(peak.date, 'YYYY-MM-DD').format('D MMM')}`}
            </>
          ) : (data && !loading ? 'Nothing in this period' : ' ')}
        </div>
        {data ? (
          <TrendChart
            days={data.days}
            color={color}
            unit={metric === 'requests' ? ['request', 'requests'] : ['activity', 'activities']}
          />
        ) : null}
      </div>
    </section>
  );
}

export default function Home(props) {
  const [filters, setFilters] = useState({ branches: [], states: [] }); // IFSC codes, state names
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [panel, setPanel] = useState({ kind: 'pending', status: 'all', open: false });
  const seq = useRef(0);

  // Query params for every dashboard call; a new object only when filters change.
  const params = useMemo(() => ({
    branches: filters.branches.join(','), states: filters.states.join(','),
  }), [filters]);

  useEffect(() => {
    seq.current += 1;
    const mine = seq.current;
    setLoading(true);
    setError('');
    get(apiList.DASHBOARD_SUMMARY, params)
      .then((d) => { if (mine === seq.current) setSummary(d); })
      .catch(() => { if (mine === seq.current) setError('Could not load the dashboard. Please try again.'); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [params]);

  const pending = summary?.pending || { Submitted: 0, Uploading: 0 };
  const pendingTotal = (pending.Submitted || 0) + (pending.Uploading || 0);
  const openPanel = (kind, status = 'all') => setPanel({ kind, status, open: true });

  verifyLogin();
  return (
    <div className='ds-page'>
      <Navbar MainProps={props} />

      <DashboardListPanel
        kind={panel.kind}
        open={panel.open}
        initialStatus={panel.status}
        onClose={() => setPanel((p) => ({ ...p, open: false }))}
        params={params}
        history={props.history}
      />

      <PageContainer width='wide' stack>
        <div className='home-head'>
          <ScopeFilters value={filters} onChange={setFilters} />
        </div>

        {error ? <div className='home-error' role='alert'>{error}</div> : null}

        <div className='home-metrics'>
          <MetricCard
            title='Activities submitted'
            icon={CloudUploadOutlinedIcon}
            value={summary?.submitted}
            loading={loading && !summary}
          />
          <MetricCard
            title='Activities verified'
            icon={VerifiedUserOutlinedIcon}
            tone='green'
            value={summary?.verified}
            loading={loading && !summary}
          />
          <MetricCard
            title='Disbursement requests'
            icon={MoveToInboxOutlinedIcon}
            tone='violet'
            value={summary?.requests}
            loading={loading && !summary}
          />
          <MetricCard
            title='Loans added'
            icon={AccountBalanceOutlinedIcon}
            tone='amber'
            value={summary?.loans}
            loading={loading && !summary}
          />
        </div>

        <div className='home-row'>
          <section className='home-card'>
            <div className='home-card-head'>
              <span className='home-icon amber' aria-hidden='true'><HourglassEmptyOutlinedIcon /></span>
              <h2>Pending activities</h2>
              <ViewAll onClick={() => openPanel('pending')} />
            </div>
            <div className='home-big'>
              <strong>{loading && !summary ? <Skeleton /> : fmt(pendingTotal)}</strong>
              not yet verified
            </div>
            <div className='home-stages'>
              <button type='button' className='home-stage' onClick={() => openPanel('pending', 'Submitted')}>
                Submitted, awaiting review
                <strong>{fmt(pending.Submitted)}</strong>
              </button>
              <button type='button' className='home-stage' onClick={() => openPanel('pending', 'Uploading')}>
                Uploading from the app
                <strong>{fmt(pending.Uploading)}</strong>
              </button>
            </div>
          </section>

          <section className='home-card'>
            <div className='home-card-head'>
              <span className='home-icon red' aria-hidden='true'><LocationOffOutlinedIcon /></span>
              <h2>Properties without location</h2>
              <ViewAll onClick={() => openPanel('missing')} />
            </div>
            <div className='home-big'>
              <strong>{loading && !summary ? <Skeleton /> : fmt(summary?.missingLocation)}</strong>
              properties
            </div>
            <p className='home-note'>
              No latitude / longitude on the loan property, so geo-tagging checks and
              the map can&apos;t place them.
            </p>
          </section>
        </div>

        <div className='home-charts'>
          <TrendCard title='Submitted activities by day' metric='submitted' color='#4582FF' params={params} />
          <TrendCard title='Verified activities by day' metric='verified' color='#12B76A' params={params} />
          <TrendCard
            title='Disbursement requests by day'
            metric='requests'
            color='#7A5AF8'
            params={params}
          />
        </div>
      </PageContainer>
    </div>
  );
}
